import React, { useImperativeHandle, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { FloatingOverlay } from '@floating-ui/react';

import { zIndex } from '@ecp/common/src/style/constant';
import boTheme from '@ecp/common/src/style/theme/bo';
import { FlexBox } from '@ecp/common/src/layouts/flex/styled';
import { TextBox } from '@ecp/common/src/text/TextBox';
import PopupUnstyled from '@ecp/common/src/components/unstyled/floating/PopupUnstyled';
import { Button } from '@ecp/common/src/components/button/bo/Button';
import LabeledCheckBox from '@ecp/common/src/components/checkbox/bo/LabeledCheckBox';
import { ReactComponent as CloseIcon } from '@ecp/common/src/assets/icon/icon__bo__close.svg';
import { ReactComponent as PopIcon } from '@ecp/common/src/assets/icon/icon__bo__pop.svg';

const Popup = (props) => {
  const {
    type,
    open,
    onClose,
    header,
    headerProps: headerComponentProps,
    closable,
    closeButtonType,
    useCancelButton,
    cancelButtonText,
    onCancelClick,
    cancelButtonProps,
    useConfirmButton,
    confirmButtonText,
    onConfirmClick,
    confirmButtonProps,
    useOtherButton,
    otherButtonText,
    onOtherClick,
    otherButtonProps,
    useScroll,
    usePopButton,
    onPopClick,
    height,
    justifyContent,
    useException,
    onExceptionChange,
    ...rest
  } = props;
  const rootStyle = useMemo(
    () => ({ ...popupRootStyle[type], ...(height ? { height } : {}), ...(justifyContent ? { justifyContent } : {}) }),
    [type, height, justifyContent]
  );

  const bottomButtonProps = {
    useCancelButton,
    cancelButtonText,
    onCancelClick,
    cancelButtonProps,
    useConfirmButton,
    confirmButtonText,
    onConfirmClick,
    confirmButtonProps,
    useOtherButton,
    otherButtonText,
    onOtherClick,
    otherButtonProps,
    useException,
    onExceptionChange,
  };

  const headerProps = {
    header,
    usePopButton,
    onPopClick,
    ...headerComponentProps,
  };

  return (
    <PopupUnstyled
      open={open}
      onClose={onClose}
      rootComponent={StyledPopupRoot}
      rootStyleProps={rootStyle}
      headerComponent={StyledHeaderComponent}
      headerProps={headerProps}
      bodyComponent={StyledBodyComponent}
      bodyProps={{ useScroll, useCancelButton, useConfirmButton }}
      bottomButtonComponent={<StyledBottomButtonComponent {...bottomButtonProps} />}
      closable={closable}
      closeComponent={closeButtonType === 'Primary' ? <DefaultCloseComponent /> : <></>}
      {...rest}
    />
  );
};

export default Popup;

Popup.displayName = 'Popup';

Popup.propTypes = {
  type: PropTypes.oneOf(['XLarge', 'Large', 'Medium', 'Small', 'Image']),
  open: PropTypes.bool,
  onClose: PropTypes.func,
  rootStyleProps: PropTypes.object,
  header: PropTypes.oneOfType([PropTypes.elementType, PropTypes.element, PropTypes.node, PropTypes.string]),
  headerProps: PropTypes.object,
  closable: PropTypes.bool,
  closeButtonType: PropTypes.oneOf(['Primary', 'Secondary']),
  useCancelButton: PropTypes.bool,
  cancelButtonText: PropTypes.string,
  onCancelClick: PropTypes.func,
  cancelButtonProps: PropTypes.object,
  useConfirmButton: PropTypes.bool,
  confirmButtonText: PropTypes.string,
  onConfirmClick: PropTypes.func,
  confirmButtonProps: PropTypes.object,
  useScroll: PropTypes.bool,
  usePopButton: PropTypes.bool,
  onPopClick: PropTypes.func,
  children: PropTypes.oneOfType([PropTypes.elementType, PropTypes.element, PropTypes.node]),
  height: PropTypes.string,
  justifyContent: PropTypes.string,
  useException: PropTypes.bool,
  onExceptionChange: PropTypes.func,
};

Popup.defaultProps = {
  type: 'Large',
  headerProps: {
    size: boTheme.font.size.medium,
    weight: boTheme.font.weight.bold,
    color: boTheme.color.sub10,
    height: '16px',
    style: { padding: '16px 30px' },
  },
  closable: true,
  closeButtonType: 'Primary',
  useCancelButton: true,
  cancelButtonProps: {
    type: 'Third',
  },
  cancelButtonText: '취소',
  useConfirmButton: true,
  confirmButtonProps: {
    type: 'Primary',
  },
  confirmButtonText: '확인',
  useOtherButton: false,
  otherButtonProps: {
    type: 'Third',
  },
  otherButtonText: '사용자정의',
  useScroll: true,
  useException: false,
};

const popupRootStyle = {
  XLarge: {
    width: '1400px',
    height: '1080px',
    maxHeight: '1080px',
  },
  Large: {
    width: '1160px',
    height: '740px',
    maxHeight: '740px',
  },
  Medium: {
    width: '700px',
    height: '680px',
    maxHeight: '680px',
  },
  Small: {
    width: '440px',
    height: '320px',
    maxHeight: '740px',
  },
  Image: {
    width: '700px',
    height: '748px',
    maxHeight: '748px',
  },
};

const StyledPopupRoot = React.forwardRef(({ children, ...props }, ref) => {
  const rootRef = useRef(null);

  useImperativeHandle(ref, () => rootRef.current);

  return (
    <FloatingOverlay
      style={{
        zIndex: zIndex.popup,
        display: 'grid',
        background: 'rgba(0, 0, 0, 0.5)',
        placeItems: 'center',
      }}
    >
      <PopupWrapper ref={rootRef} {...props}>
        {children}
      </PopupWrapper>
    </FloatingOverlay>
  );
});

StyledPopupRoot.displayName = 'StyledPopupRoot';

StyledPopupRoot.propTypes = {
  nodeId: PropTypes.string,
  children: PropTypes.node,
};

const PopupWrapper = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  width: fit-content;
  height: fit-content;
  background-color: #ffffff;

  &:focus-visible {
    outline: none;
  }
`;

const StyledHeaderComponent = ({ header, usePopButton, onPopClick, ...props }) => {
  return (
    <>
      <TextBox background-color={boTheme.color.menu1} {...props}>
        {header}
      </TextBox>
      {usePopButton && <DefaultPopComponent onClick={onPopClick} />}
    </>
  );
};

StyledHeaderComponent.propTypes = {
  header: PropTypes.oneOfType([PropTypes.element, PropTypes.string]),
  usePopButton: PropTypes.bool,
  onPopClick: PropTypes.func,
};

const StyledBodyComponent = styled(FlexBox)`
  justify-content: flex-start;
  align-items: flex-start;
  flex: 1;
  overflow-y: ${({ useScroll }) => (useScroll ? 'auto' : 'hidden')};
  padding: 0 30px;
  margin: 30px 0;
  ${({ useCancelButton, useConfirmButton }) => (useCancelButton || useConfirmButton) && `margin-bottom: 0;`}
`;

export const StyledBottomButtonComponent = (props) => {
  const {
    useCancelButton,
    cancelButtonProps,
    onCancelClick,
    cancelButtonText,
    useConfirmButton,
    confirmButtonProps,
    onConfirmClick,
    confirmButtonText,
    useOtherButton,
    otherButtonText,
    onOtherClick,
    otherButtonProps,
    gap = '8px',
    padding = '16px 30px',
    useException,
    onExceptionChange,
  } = props;

  const [exceptionApi, setExceptionApi] = useState('N');

  const labelProp = () => {
    return {
      style: {
        paddingLeft: '2px',
      },
      size: `${boTheme.font.size.small}`,
      color: `${boTheme.color.sub15}`,
      height: '20px',
    };
  };

  return (
    <>
      {(useCancelButton || useConfirmButton) && (
        <FlexBox gap={gap} padding={padding} justify-content={'flex-end'}>
          {useException && (
            <FlexBox>
              <LabeledCheckBox
                labelText={'외부 연동 예외\n*쿠프/기프티쇼/이니시스에 주문 취소가 요청 되지 않습니다.'}
                labelProps={labelProp()}
                checked={exceptionApi === 'Y'}
                onChange={(e) => {
                  const resultFlag = e.target.checked ? 'Y' : 'N';
                  setExceptionApi(resultFlag);
                  onExceptionChange && onExceptionChange(resultFlag);
                }}
              />
            </FlexBox>
          )}
          {useCancelButton && (
            <Button {...cancelButtonProps} onClick={onCancelClick}>
              {cancelButtonText}
            </Button>
          )}
          {useOtherButton && (
            <Button {...otherButtonProps} onClick={onOtherClick}>
              {otherButtonText}
            </Button>
          )}
          {useConfirmButton && (
            <Button {...confirmButtonProps} onClick={onConfirmClick}>
              {confirmButtonText}
            </Button>
          )}
        </FlexBox>
      )}
    </>
  );
};

StyledBottomButtonComponent.propTypes = {
  useCancelButton: PropTypes.bool,
  cancelButtonProps: PropTypes.object,
  onCancelClick: PropTypes.func,
  cancelButtonText: PropTypes.string,
  useConfirmButton: PropTypes.bool,
  confirmButtonProps: PropTypes.object,
  onConfirmClick: PropTypes.func,
  confirmButtonText: PropTypes.string,
  useOtherButton: PropTypes.bool,
  otherButtonProps: PropTypes.object,
  onOtherClick: PropTypes.func,
  otherButtonText: PropTypes.string,
  gap: PropTypes.string,
  padding: PropTypes.string,
  useException: PropTypes.bool,
  onExceptionChange: PropTypes.func,
};

const DefaultCloseComponent = styled(CloseIcon)`
  border: none;
  width: 16px;
  height: 16px;
  background-color: transparent;
  position: absolute;
  top: 16px;
  right: 30px;
  cursor: pointer;
`;

const DefaultPopComponent = styled(PopIcon)`
  border: none;
  width: 16px;
  height: 16px;
  background-color: transparent;
  position: absolute;
  top: 16px;
  right: 62px;
  cursor: pointer;
`;
